import React, { useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LayoutGrid, Activity, BookOpen, History, Target, Apple, LineChart, User, LogOut,
  Ruler, Music, Crown, HelpCircle, Menu, X, Camera, Award,
} from "lucide-react";
import ThemeToggle from "./ThemeToggle";
import BrandMark from "./BrandMark";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "./ui/drawer";

const links = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutGrid, testid: "nav-dashboard" },
  { to: "/app/workout", label: "Workout", icon: Activity, testid: "nav-workout" },
  { to: "/app/library", label: "Library", icon: BookOpen, testid: "nav-library" },
  { to: "/app/history", label: "History", icon: History, testid: "nav-history" },
  { to: "/app/goals", label: "Goals", icon: Target, testid: "nav-goals" },
  { to: "/app/achievements", label: "Achievements", icon: Award, testid: "nav-achievements" },
  { to: "/app/measurements", label: "Measurements", icon: Ruler, testid: "nav-measurements" },
  { to: "/app/progress-photos", label: "Photos", icon: Camera, testid: "nav-photos" },
  { to: "/app/music", label: "Music", icon: Music, testid: "nav-music" },
  { to: "/app/nutrition", label: "Nutrition", icon: Apple, testid: "nav-nutrition" },
  { to: "/app/progress", label: "Progress", icon: LineChart, testid: "nav-progress" },
  { to: "/app/profile", label: "Profile", icon: User, testid: "nav-profile" },
  { to: "/app/billing", label: "Billing", icon: Crown, testid: "nav-billing" },
  { to: "/app/tutorial", label: "Tutorial", icon: HelpCircle, testid: "nav-tutorial" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Resolve the currently-active nav entry so the mobile bar can show context
  const activeLink =
    links.find((l) => pathname.startsWith(l.to)) || links[0];

  const closeDrawer = () => setDrawerOpen(false);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex">
      <aside className="w-64 border-r border-white/10 bg-[#0a0a0a] hidden md:flex flex-col">
        <div className="h-16 px-6 flex items-center border-b border-white/10">
          <BrandMark size={32} />
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                  isActive
                    ? "bg-[#121212] text-[#6B9BFF] border-l-2 border-[#6B9BFF]"
                    : "text-white/60 hover:text-white hover:bg-[#121212] border-l-2 border-transparent"
                }`
              }
            >
              <l.icon className="w-4 h-4" />
              <span className="uppercase tracking-wider font-bold text-xs">{l.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center justify-between mb-3 px-2">
            <div>
              <div className="text-xs text-white/40 label-eyebrow">Signed in</div>
              <div className="text-sm text-white truncate max-w-[140px]">{user?.name}</div>
            </div>
            <ThemeToggle />
          </div>
          <button
            onClick={() => {
              logout();
              navigate("/");
            }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-white/60 hover:text-white hover:bg-[#121212] transition-colors"
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4" />
            <span className="uppercase tracking-wider font-bold text-xs">Logout</span>
          </button>
        </div>
      </aside>

      {/* ============ Mobile bottom bar + openable menu drawer ============ */}
      <div
        className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-black/90 backdrop-blur-xl border-t border-white/10"
        data-testid="mobile-bottom-bar"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="flex items-center justify-between px-4 py-3 gap-3">
          {/* Menu button (opens bottom drawer) — placed LEFT so it doesn't
              collide with the Emergent badge / Spotify pill anchored bottom-right */}
          <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
            <DrawerTrigger asChild>
              <button
                type="button"
                style={{ backgroundColor: "var(--accent)", color: "var(--accent-fg)" }}
                className="flex items-center gap-2 font-bold uppercase tracking-widest text-xs px-5 py-3 hover:opacity-90 transition-opacity shrink-0"
                data-testid="mobile-menu-button"
                aria-label="Open menu"
              >
                <Menu className="w-4 h-4" />
                Menu
              </button>
            </DrawerTrigger>
            <DrawerContent
              className="bg-[#0a0a0a] border-t border-white/10 text-white max-h-[88vh]"
              data-testid="mobile-menu-drawer"
            >
              <DrawerHeader className="px-6 pt-2 pb-4 flex items-center justify-between">
                <div className="text-left">
                  <div className="label-eyebrow text-white/40">Navigate</div>
                  <DrawerTitle className="font-display text-2xl text-white mt-1">
                    All sections
                  </DrawerTitle>
                </div>
                <button
                  type="button"
                  onClick={closeDrawer}
                  className="w-9 h-9 flex items-center justify-center text-white/50 hover:text-white border border-white/10"
                  data-testid="mobile-menu-close"
                  aria-label="Close menu"
                >
                  <X className="w-4 h-4" />
                </button>
              </DrawerHeader>

              <nav
                className="px-4 grid grid-cols-2 gap-2 pb-2"
                data-testid="mobile-menu-grid"
              >
                {links.map((l) => {
                  const isActive = pathname.startsWith(l.to);
                  return (
                    <NavLink
                      key={l.to}
                      to={l.to}
                      onClick={closeDrawer}
                      data-testid={`m-${l.testid}`}
                      style={
                        isActive
                          ? {
                              borderColor: "var(--accent)",
                              backgroundColor: "color-mix(in srgb, var(--accent) 12%, transparent)",
                              color: "var(--accent)",
                            }
                          : undefined
                      }
                      className={`flex items-center gap-3 p-4 border transition-colors ${
                        isActive
                          ? ""
                          : "border-white/10 text-white/80 hover:border-white/25"
                      }`}
                    >
                      <l.icon className="w-4 h-4 shrink-0" />
                      <span className="uppercase tracking-wider font-bold text-xs">
                        {l.label}
                      </span>
                    </NavLink>
                  );
                })}
              </nav>

              <div className="px-4 pt-5 pb-6 mt-2 border-t border-white/10">
                <div className="flex items-center justify-between mb-3 px-1">
                  <div className="min-w-0">
                    <div className="label-eyebrow text-white/40">Signed in</div>
                    <div className="text-sm text-white truncate max-w-[180px]">
                      {user?.name}
                    </div>
                  </div>
                  <ThemeToggle />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    closeDrawer();
                    logout();
                    navigate("/");
                  }}
                  className="w-full flex items-center justify-center gap-3 px-4 py-3 text-sm text-white/70 hover:text-white border border-white/10 hover:border-white/25 transition-colors"
                  data-testid="mobile-logout-button"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="uppercase tracking-wider font-bold text-xs">Logout</span>
                </button>
              </div>
            </DrawerContent>
          </Drawer>

          {/* Current page indicator — right side, hidden text on tiny screens */}
          <div className="flex items-center gap-2 min-w-0 justify-end">
            <div className="min-w-0 text-right hidden xs:block">
              <div className="label-eyebrow text-white/40 text-[9px]">Current</div>
              <div
                className="text-xs font-bold text-white truncate max-w-[120px]"
                data-testid="mobile-current-page"
              >
                {activeLink.label}
              </div>
            </div>
            <div
              className="w-9 h-9 shrink-0 border flex items-center justify-center"
              style={{
                borderColor: "color-mix(in srgb, var(--accent) 40%, transparent)",
                backgroundColor: "color-mix(in srgb, var(--accent) 12%, transparent)",
              }}
            >
              <activeLink.icon className="w-4 h-4" style={{ color: "var(--accent)" }} />
            </div>
          </div>
        </div>
      </div>

      <main className="flex-1 min-w-0 pb-20 md:pb-0">
        <Outlet />
      </main>
    </div>
  );
}
